<template>
    <div class="support-page">
        <!-- 1. 顶部搜索引导区 (Hero Search) -->
        <section class="support-hero">
            <div class="hero-content">
                <h1>How can we help you today?</h1>
                <div class="search-box">
                    <span class="search-icon">🔍</span>
                    <input type="text" placeholder="Search for devices, troubleshooting, or guides..."
                        v-model="searchQuery" />
                    <button class="btn-search">Search</button>
                </div>
                <div class="popular-searches">
                    <span>Popular:</span>
                    <a href="#">Hub Offline</a>
                    <a href="#">Reset Device</a>
                    <a href="#">Firmware Update</a>
                </div>
            </div>
        </section>

        <!-- 2. 帮助中心主分类 (Help Categories) -->
        <section class="categories-section container">
            <div class="grid-container grid-4">
                <div class="category-card" v-for="(cat, index) in categories" :key="index">
                    <div class="cat-icon">{{ cat.icon }}</div>
                    <h3>{{ cat.title }}</h3>
                    <p>{{ cat.desc }}</p>
                </div>
            </div>
        </section>

        <!-- 3. 开发者与技术支持区 (Developer & Integrator Resources) -->
        <section class="developer-section bg-light">
            <div class="container">
                <h2 class="section-title">Developer & Integrator Resources</h2>
                <p class="section-subtitle">SDKs, frameworks, and rendering guidelines for custom hardware and app
                    integration.</p>

                <div class="grid-container grid-3">
                    <div class="resource-card" v-for="(resource, index) in devResources" :key="index">
                        <h4 class="resource-title">{{ resource.title }}</h4>
                        <p class="resource-desc">{{ resource.desc }}</p>
                        <a href="#" class="resource-link">View Documentation →</a>
                    </div>
                </div>
            </div>
        </section>

        <!-- 4. 常见问题解答 (FAQ Accordion) -->
        <section class="faq-section container">
            <h2 class="section-title">Frequently Asked Questions</h2>
            <div class="faq-list">
                <div class="faq-item" v-for="(faq, index) in faqs" :key="index"
                    :class="{ 'is-open': activeFaq === index }">
                    <div class="faq-question" @click="toggleFaq(index)">
                        <h3>{{ faq.q }}</h3>
                        <span class="toggle-icon">{{ activeFaq === index ? '−' : '+' }}</span>
                    </div>
                    <transition name="accordion">
                        <div class="faq-answer" v-show="activeFaq === index">
                            <p>{{ faq.a }}</p>
                        </div>
                    </transition>
                </div>
            </div>
        </section>

        <!-- 5. 人工支持与联系方式 (Contact Us) -->
        <section class="contact-section container">
            <div class="contact-box">
                <h2>Still need help?</h2>
                <p>Our technical support team is available 24/7 to assist you.</p>
                <div class="contact-methods">
                    <button class="btn-outline">💬 Live Chat</button>
                    <button class="btn-outline">✉️ Email Support</button>
                    <button class="btn-outline">📞 +1 (800) 123-4567</button>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>
import { ref } from 'vue'

const searchQuery = ref('')
const activeFaq = ref(0) // 默认展开第一个 FAQ

const toggleFaq = (index) => {
    activeFaq.value = activeFaq.value === index ? -1 : index
}

// 基础分类数据
const categories = ref([
    { icon: '📦', title: 'Getting Started', desc: 'Device pairing, app setup, and account configuration.' },
    { icon: '🔧', title: 'Troubleshooting', desc: 'Fix offline devices, connection drops, and hardware issues.' },
    { icon: '🔄', title: 'Returns & Warranty', desc: 'Check warranty status and initiate return requests.' },
    { icon: '📚', title: 'User Manuals', desc: 'Download PDF guides and detailed product specifications.' }
])

// 技术与开发者资源数据
const devResources = ref([
    {
        title: 'Zigbee Application Framework',
        desc: 'Documentation and compilation guides for deploying ZAF v6.10.3 into Android 8.1 environments for local device control.'
    },
    {
        title: 'Mobile App SDK (Flutter)',
        desc: 'Integrate device control and real-time status listening directly into your cross-platform Flutter applications.'
    },
    {
        title: '3D Asset Rendering',
        desc: 'Best practices and troubleshooting for importing .obj and .fbx device models into your frontend WebGL renderers.'
    }
])

// 常见问题数据
const faqs = ref([
    { q: 'How do I factory reset my smart gateway?', a: 'Press and hold the reset button on the back of the gateway for 10 seconds until the LED indicator flashes red rapidly. You will need to re-pair all your child devices afterward.' },
    { q: 'Why is my device showing "Offline" in the app?', a: 'This usually happens due to Wi-Fi signal interference or a router restart. Please check if your 2.4GHz network is active, or try bringing the device closer to the router.' },
    { q: 'Can I control my devices when the internet is down?', a: 'Yes. As long as your phone and the smart hub are connected to the same local Wi-Fi network, local control will remain active for most automation scenes.' },
    { q: 'How do I update the firmware of my devices?', a: 'Open the app, go to Settings > Firmware Update. If a new version is available, a red dot will appear. Tap "Update All" and ensure your devices have sufficient battery.' }
])
</script>

<style scoped>
/* =========================================
   全局与基础样式
========================================= */
.support-page {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    color: #333;
    line-height: 1.6;
}

.container {
    max-width: 1100px;
    margin: 0 auto;
    padding: 80px 20px;
}

.bg-light {
    background-color: #f9fafb;
}

.section-title {
    text-align: center;
    font-size: 2rem;
    margin-bottom: 15px;
    font-weight: 700;
    color: #111;
}

.section-subtitle {
    text-align: center;
    font-size: 1.1rem;
    color: #666;
    margin-bottom: 50px;
}

/* Grid 系统 */
.grid-container {
    display: grid;
    gap: 30px;
}

.grid-4 {
    grid-template-columns: repeat(4, 1fr);
}

.grid-3 {
    grid-template-columns: repeat(3, 1fr);
}

/* =========================================
   1. 顶部搜索区 (Hero Search)
========================================= */
.support-hero {
    background-color: #1a1a1a;
    color: #fff;
    padding: 100px 20px;
    text-align: center;
}

.hero-content {
    max-width: 800px;
    margin: 0 auto;
}

.hero-content h1 {
    font-size: 2.8rem;
    margin-bottom: 30px;
    font-weight: 600;
}

.search-box {
    display: flex;
    align-items: center;
    background: #fff;
    border-radius: 8px;
    padding: 8px 8px 8px 20px;
    margin-bottom: 20px;
}

.search-icon {
    font-size: 1.2rem;
    color: #999;
    margin-right: 10px;
}

.search-box input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 1.1rem;
    color: #333;
    padding: 10px 0;
}

.btn-search {
    background: #007aff;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 12px 30px;
    font-size: 1rem;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.2s;
}

.btn-search:hover {
    background: #006ae6;
}

.popular-searches {
    font-size: 0.9rem;
    color: #aaa;
}

.popular-searches a {
    color: #fff;
    text-decoration: none;
    margin-left: 15px;
    border-bottom: 1px dashed #666;
    transition: color 0.2s;
}

.popular-searches a:hover {
    color: #007aff;
    border-color: #007aff;
}

/* =========================================
   2. 帮助分类卡片
========================================= */
.category-card {
    background: #fff;
    border: 1px solid #eaeaea;
    border-radius: 12px;
    padding: 30px 20px;
    text-align: center;
    transition: all 0.3s ease;
    cursor: pointer;
}

.category-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.08);
    border-color: transparent;
}

.cat-icon {
    font-size: 2.5rem;
    margin-bottom: 20px;
}

.category-card h3 {
    font-size: 1.2rem;
    margin-bottom: 10px;
    color: #111;
}

.category-card p {
    font-size: 0.9rem;
    color: #666;
}

/* =========================================
   3. 开发者资源卡片
========================================= */
.developer-section {
    padding: 80px 20px;
    border-top: 1px solid #eaeaea;
    border-bottom: 1px solid #eaeaea;
}

.resource-card {
    background: #fff;
    padding: 30px;
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
    display: flex;
    flex-direction: column;
}

.resource-title {
    font-size: 1.1rem;
    margin-bottom: 15px;
    color: #111;
}

.resource-desc {
    font-size: 0.95rem;
    color: #666;
    margin-bottom: 25px;
    flex: 1;
}

.resource-link {
    color: #007aff;
    text-decoration: none;
    font-weight: 600;
    font-size: 0.9rem;
}

.resource-link:hover {
    text-decoration: underline;
}

/* =========================================
   4. 手风琴 FAQ (Accordion)
========================================= */
.faq-section {
    max-width: 800px;
}

.faq-list {
    border-top: 1px solid #eaeaea;
    margin-top: 40px;
}

.faq-item {
    border-bottom: 1px solid #eaeaea;
}

.faq-question {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 25px 0;
    cursor: pointer;
    user-select: none;
    transition: color 0.2s;
}

.faq-question h3 {
    font-size: 1.1rem;
    font-weight: 500;
    margin: 0;
    color: #111;
}

.faq-question:hover h3 {
    color: #007aff;
}

.toggle-icon {
    font-size: 1.5rem;
    color: #999;
    font-weight: 300;
}

.faq-answer {
    padding-bottom: 25px;
    color: #555;
    font-size: 1rem;
    line-height: 1.7;
}

/* Vue 手风琴动画 */
.accordion-enter-active,
.accordion-leave-active {
    transition: all 0.3s ease;
    overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
}

.accordion-enter-to,
.accordion-leave-from {
    opacity: 1;
    max-height: 200px;
    transform: translateY(0);
}

/* =========================================
   5. 联系我们 (Contact)
========================================= */
.contact-section {
    padding-top: 40px;
    padding-bottom: 100px;
}

.contact-box {
    background: #f4f5f7;
    border-radius: 20px;
    padding: 60px 40px;
    text-align: center;
}

.contact-box h2 {
    font-size: 1.8rem;
    margin-bottom: 15px;
}

.contact-box p {
    color: #666;
    margin-bottom: 40px;
}

.contact-methods {
    display: flex;
    justify-content: center;
    gap: 20px;
    flex-wrap: wrap;
}

.btn-outline {
    padding: 12px 25px;
    border: 1px solid #d9d9d9;
    background: #fff;
    border-radius: 8px;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s;
    font-weight: 500;
    color: #333;
}

.btn-outline:hover {
    border-color: #007aff;
    color: #007aff;
    box-shadow: 0 4px 12px rgba(0, 122, 255, 0.1);
}

/* =========================================
   响应式适配
========================================= */
@media (max-width: 992px) {
    .grid-4 {
        grid-template-columns: repeat(2, 1fr);
    }

    .grid-3 {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .hero-content h1 {
        font-size: 2.2rem;
    }

    .search-box {
        flex-direction: column;
        padding: 10px;
        background: transparent;
    }

    .search-box input {
        width: 100%;
        padding: 15px;
        border-radius: 8px;
        margin-bottom: 10px;
    }

    .btn-search {
        width: 100%;
    }

    .grid-4,
    .grid-3 {
        grid-template-columns: 1fr;
    }

    .contact-methods {
        flex-direction: column;
    }

    .btn-outline {
        width: 100%;
    }
}
</style>